/**
 * scope انتخاب‌شده در حالت کارگاه: کدام سازمان و کدام دستیار.
 *
 * در `sessionStorage` نگه داشته می‌شود تا جابه‌جایی بین مسیرهای استودیو و
 * بارگذاری دوباره‌ی صفحه، انتخاب را از دست ندهد. با `useState` تنها، هر
 * ناوبری کاربر را به انتخاب دوباره مجبور می‌کرد.
 *
 * ⚠️ در حالت `assistant` این زمینه بی‌معناست و مقدارش خالی می‌ماند. نسخه‌ی
 * مشتری تک‌دستیار است و selector ندارد.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

import type { Scope } from './api/endpoints'

const KEY = 'qatran.studio.scope'

interface ScopeContextValue extends Scope {
  /** آیا هر دو شناسه انتخاب شده‌اند؟ صفحات پرونده و انتشار به این نیاز دارند. */
  isComplete: boolean
  scope: Scope
  selectOrganization: (organizationId: string) => void
  selectAssistant: (assistantId: string) => void
}

const ScopeContext = createContext<ScopeContextValue | null>(null)

const read = (): Scope => {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    if (raw === null) {
      return { organizationId: '', assistantId: '' }
    }
    const parsed = JSON.parse(raw) as Partial<Scope>
    return {
      organizationId: parsed.organizationId ?? '',
      assistantId: parsed.assistantId ?? '',
    }
  } catch {
    return { organizationId: '', assistantId: '' }
  }
}

const write = (scope: Scope): void => {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(scope))
  } catch {
    // دسترسی به storage ممکن است ممنوع باشد؛ در آن حالت انتخاب فقط در حافظه
    // می‌ماند و با بارگذاری دوباره پاک می‌شود.
  }
}

export const ScopeProvider = ({ children }: { children: ReactNode }) => {
  const [scope, setScope] = useState<Scope>(read)

  const selectOrganization = useCallback((organizationId: string) => {
    // عوض کردن سازمان، دستیار را پاک می‌کند. بدون این، دستیار سازمان قبلی
    // انتخاب می‌ماند و درخواست بعدی ۴۰۴ می‌گرفت.
    setScope((current) => {
      const next =
        current.organizationId === organizationId
          ? current
          : { organizationId, assistantId: '' }
      write(next)
      return next
    })
  }, [])

  const selectAssistant = useCallback((assistantId: string) => {
    setScope((current) => {
      const next = { ...current, assistantId }
      write(next)
      return next
    })
  }, [])

  const value = useMemo<ScopeContextValue>(
    () => ({
      ...scope,
      scope,
      isComplete: scope.organizationId !== '' && scope.assistantId !== '',
      selectOrganization,
      selectAssistant,
    }),
    [scope, selectOrganization, selectAssistant],
  )

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>
}

export const useScope = (): ScopeContextValue => {
  const value = useContext(ScopeContext)
  if (value === null) {
    throw new Error('useScope باید داخل ScopeProvider استفاده شود.')
  }
  return value
}
